import axios from 'axios';
import { User, Team, Task, CreateUserDto, CreateTeamDto, CreateTaskDto, TaskComment, TaskFeedback, TaskFinishDetail } from '../types';
import config from '../config/environment';

const API_BASE_URL = config.apiBaseUrl;

// Debug API base URL
console.log('API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Debug axios instance
console.log('Axios baseURL:', api.defaults.baseURL);

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
const authAPI = {
  login: async (email: string, password: string) => {
    console.log('API: Making login request to:', `${API_BASE_URL}/auth/login`);
    console.log('API: Request data:', { email, password });
    const response = await api.post('/auth/login', { email, password });
    console.log('API: Response:', response.data);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Users API
const usersAPI = {
  getAll: async (limit?: number, recent?: boolean): Promise<User[]> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (recent) params.append('recent', 'true');

    const response = await api.get(`/users${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  create: async (userData: CreateUserDto): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  update: async (id: string, userData: Partial<CreateUserDto>): Promise<User> => {
    const response = await api.patch(`/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
  
  getUserTasks: async (id: string): Promise<Task[]> => {
    const response = await api.get(`/users/${id}/tasks`);
    return response.data;
  },

  getUserPerformance: async (id: string): Promise<any> => {
    const response = await api.get(`/users/${id}/performance`);
    return response.data;
  },

  getUserStatistics: async (id: string): Promise<any> => {
    const response = await api.get(`/users/${id}/statistics`);
    return response.data;
  },

  getUserDashboard: async (id: string): Promise<any> => {
    const response = await api.get(`/users/${id}/dashboard`);
    return response.data;
  },

  getUserProjects: async (id: string): Promise<any> => {
    const response = await api.get(`/users/${id}/projects`);
    return response.data;
  },

  getUserTeamInsights: async (id: string): Promise<any> => {
    const response = await api.get(`/users/${id}/teams/insights`);
    return response.data;
  },

  getUserDeadlines: async (id: string, days?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (days) params.append('days', days.toString());
    const response = await api.get(`/users/${id}/deadlines${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getUserRecentAssignments: async (id: string, limit?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/users/${id}/recent-assignments${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },

  getUserCriticalTasks: async (id: string, limit?: number): Promise<any> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/users/${id}/critical-tasks${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },
};

// Teams API
const teamsAPI = {
  getAll: async (): Promise<Team[]> => {
    const response = await api.get('/teams');
    return response.data;
  },
  
  getById: async (id: string): Promise<Team> => {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  },
  
  create: async (teamData: CreateTeamDto): Promise<Team> => {
    const response = await api.post('/teams', teamData);
    return response.data;
  },
  
  update: async (id: string, teamData: Partial<CreateTeamDto>): Promise<Team> => {
    const response = await api.patch(`/teams/${id}`, teamData);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/teams/${id}`);
  },
  


  getTeamPerformance: async (teamId: string): Promise<any> => {
    const response = await api.get(`/teams/${teamId}/performance`);
    return response.data;
  },

  getTeamStatistics: async (teamId: string): Promise<any> => {
    const response = await api.get(`/teams/${teamId}/statistics`);
    return response.data;
  },

  getTeamAnalytics: async (teamId: string): Promise<any> => {
    const response = await api.get(`/teams/${teamId}/analytics`);
    return response.data;
  },
  
  getTeamTasks: async (id: string): Promise<Task[]> => {
    const response = await api.get(`/teams/${id}/tasks`);
    return response.data;
  },

  getTeamProjects: async (id: string): Promise<any[]> => {
    const response = await api.get(`/teams/${id}/projects`);
    return response.data;
  },

  addMembers: async (id: string, data: { userIds: string[] }): Promise<any> => {
    const response = await api.post(`/teams/${id}/members`, data);
    return response.data;
  },

  removeMember: async (teamId: string, userId: string): Promise<any> => {
    const response = await api.delete(`/teams/${teamId}/members/${userId}`);
    return response.data;
  },
};

// Tasks API
const tasksAPI = {
  getAll: async (filters?: {
    teamId?: string;
    projectId?: string;
    assigneeId?: string;
    status?: string;
    priority?: string;
    limit?: number;
    page?: number;
    offset?: number;
    critical?: boolean;
    includeProjectTasks?: boolean;
  }): Promise<Task[] | { data: Task[]; total: number; page: number; limit: number; totalPages: number }> => {
    const params = new URLSearchParams();
    if (filters?.teamId) params.append('teamId', filters.teamId);
    if (filters?.projectId) params.append('projectId', filters.projectId);
    if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.critical) params.append('critical', 'true');
    if (filters?.includeProjectTasks !== undefined) params.append('includeProjectTasks', filters.includeProjectTasks.toString());

    const response = await api.get(`/tasks?${params.toString()}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },
  
  create: async (taskData: CreateTaskDto): Promise<Task> => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },
  
  update: async (id: string, taskData: Partial<CreateTaskDto>): Promise<Task> => {
    const response = await api.patch(`/tasks/${id}`, taskData);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },
  
  assignUsers: async (id: string, userIds: string[]): Promise<Task> => {
    const response = await api.post(`/tasks/${id}/assign`, { userIds });
    return response.data;
  },
  
  unassignUser: async (id: string, userId: string): Promise<Task> => {
    const response = await api.delete(`/tasks/${id}/assign/${userId}`);
    return response.data;
  },

  getTaskAnalytics: async (): Promise<any> => {
    const response = await api.get('/tasks/analytics/overview');
    return response.data;
  },

  getProgressAnalytics: async (): Promise<any> => {
    const response = await api.get('/tasks/analytics/progress');
    return response.data;
  },

  getPerformanceAnalytics: async (): Promise<any> => {
    const response = await api.get('/tasks/analytics/performance');
    return response.data;
  },

  getTaskHistory: async (taskId: string): Promise<any> => {
    const response = await api.get(`/tasks/${taskId}/history`);
    return response.data;
  },

  getCalendarEvents: async (start?: string, end?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await api.get(`/tasks/calendar/events?${params}`);
    return response.data;
  },

  getMonthTasks: async (year: number, month: number, assigneeId?: string): Promise<any> => {
    const params = new URLSearchParams();
    if (assigneeId) params.append('assigneeId', assigneeId);

    const url = `/tasks/calendar/month/${year}/${month}${params.toString() ? `?${params.toString()}` : ''}`;
    console.log('API call URL:', url);
    console.log('API call params:', { year, month, assigneeId });
    const response = await api.get(url);
    console.log('API response:', response.data);
    return response.data;
  },

  // Enhanced task management methods
  updateTaskTime: async (id: string, timeData: { estimatedHours?: number; actualHours?: number; progressPercentage?: number }): Promise<any> => {
    const response = await api.patch(`/tasks/${id}/time`, timeData);
    return response.data;
  },

  getTaskFeedbacks: async (id: string): Promise<any> => {
    const response = await api.get(`/tasks/${id}/feedbacks`);
    return response.data;
  },

  addTaskFeedback: async (id: string, feedbackData: { content: string; userId: string }): Promise<any> => {
    const response = await api.post(`/tasks/${id}/user-feedbacks`, feedbackData);
    return response.data;
  },

  removeTaskFeedback: async (feedbackId: string): Promise<any> => {
    const response = await api.delete(`/tasks/user-feedbacks/${feedbackId}`);
    return response.data;
  },

  getTaskFinishDetails: async (id: string): Promise<any> => {
    const response = await api.get(`/tasks/${id}/finish-details`);
    return response.data;
  },

  addTaskFinishDetail: async (id: string, finishData: { description: string; notes?: string; userId: string }): Promise<any> => {
    const response = await api.post(`/tasks/${id}/user-finish-details`, finishData);
    return response.data;
  },

  updateTaskFinishDetail: async (detailId: string, updateData: { description?: string; notes?: string }): Promise<any> => {
    const response = await api.patch(`/tasks/user-finish-details/${detailId}`, updateData);
    return response.data;
  },

  removeTaskFinishDetail: async (detailId: string): Promise<any> => {
    const response = await api.delete(`/tasks/user-finish-details/${detailId}`);
    return response.data;
  },

  updateTaskStatus: async (id: string, statusData: {
    status: string;
    completionDetails?: { description: string; notes?: string };
    userId: string
  }): Promise<any> => {
    const response = await api.patch(`/tasks/${id}/status`, statusData);
    return response.data;
  },

  getUserTasksEnhanced: async (userId: string, filters?: {
    status?: string;
    priority?: string;
    projectId?: string;
    teamId?: string;
    includeCompleted?: boolean;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
  }): Promise<any> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.projectId) params.append('projectId', filters.projectId);
    if (filters?.teamId) params.append('teamId', filters.teamId);
    if (filters?.includeCompleted !== undefined) params.append('includeCompleted', filters.includeCompleted.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`/tasks/user/${userId}${params.toString() ? '?' + params.toString() : ''}`);
    return response.data;
  },
  
  addComment: async (id: string, content: string, userId: string): Promise<TaskComment> => {
    const response = await api.post(`/tasks/${id}/comments`, { content, userId });
    return response.data;
  },
  
  getComments: async (id: string): Promise<TaskComment[]> => {
    const response = await api.get(`/tasks/${id}/comments`);
    return response.data;
  },

  // Task Feedback API methods
  createFeedback: async (taskId: string, content: string, userId: string): Promise<TaskFeedback> => {
    const response = await api.post(`/tasks/${taskId}/feedbacks`, { content, userId });
    return response.data;
  },

  getFeedbacks: async (taskId: string): Promise<TaskFeedback[]> => {
    const response = await api.get(`/tasks/${taskId}/feedbacks`);
    return response.data;
  },

  updateFeedback: async (feedbackId: string, content: string, userId: string): Promise<TaskFeedback> => {
    const response = await api.patch(`/tasks/feedbacks/${feedbackId}`, { content, userId });
    return response.data;
  },

  deleteFeedback: async (feedbackId: string, userId: string): Promise<void> => {
    await api.delete(`/tasks/feedbacks/${feedbackId}`, { data: { userId } });
  },

  // Task Finish Detail API methods
  createFinishDetail: async (taskId: string, description: string, notes: string | undefined, userId: string, completedAt?: string): Promise<TaskFinishDetail> => {
    const response = await api.post(`/tasks/${taskId}/finish-details`, {
      description,
      notes,
      userId,
      completedAt
    });
    return response.data;
  },

  getFinishDetails: async (taskId: string): Promise<TaskFinishDetail[]> => {
    const response = await api.get(`/tasks/${taskId}/finish-details`);
    return response.data;
  },

  updateFinishDetail: async (finishDetailId: string, description: string, notes?: string, completedAt?: string): Promise<TaskFinishDetail> => {
    const response = await api.patch(`/tasks/finish-details/${finishDetailId}`, {
      description,
      notes,
      completedAt
    });
    return response.data;
  },

  deleteFinishDetail: async (finishDetailId: string): Promise<void> => {
    await api.delete(`/tasks/finish-details/${finishDetailId}`);
  },

  // Recurring Task API methods
  createRecurringTask: async (recurringTaskData: any): Promise<any> => {
    const response = await api.post('/tasks/recurring', recurringTaskData);
    return response.data;
  },

  getRecurringTasks: async (includeProjectTasks: boolean = true, filters?: {
    assigneeId?: string;
    limit?: number;
    page?: number;
    offset?: number;
  }): Promise<any[] | { data: any[]; total: number; page: number; limit: number; totalPages: number }> => {
    const params = new URLSearchParams();
    params.append('includeProjectTasks', includeProjectTasks.toString());
    if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get(`/tasks/recurring?${params.toString()}`);
    return response.data;
  },

  getRecurringTask: async (id: string): Promise<any> => {
    const response = await api.get(`/tasks/recurring/${id}`);
    return response.data;
  },

  updateRecurringTask: async (id: string, updateData: any): Promise<any> => {
    const response = await api.put(`/tasks/recurring/${id}`, updateData);
    return response.data;
  },

  deleteRecurringTask: async (id: string): Promise<void> => {
    await api.delete(`/tasks/recurring/${id}`);
  },

  generateRecurringTasks: async (id: string): Promise<void> => {
    await api.post(`/tasks/recurring/${id}/generate`);
  },

  generateAllUpcomingRecurringTasks: async (): Promise<void> => {
    await api.post('/tasks/recurring/generate-all');
  },

  cleanupGeneratedRecurringTasks: async (): Promise<any> => {
    const response = await api.delete('/tasks/recurring/cleanup-generated');
    return response.data;
  },

  // Recurring Task Feedback APIs
  addRecurringTaskFeedback: async (templateId: string, content: string, authorId: string): Promise<any> => {
    const response = await api.post(`/tasks/recurring/${templateId}/feedbacks`, { content, authorId });
    return response.data;
  },

  getRecurringTaskFeedbacks: async (templateId: string): Promise<any[]> => {
    const response = await api.get(`/tasks/recurring/${templateId}/feedbacks`);
    return response.data;
  },

  deleteRecurringTaskFeedback: async (feedbackId: string, userId: string): Promise<void> => {
    await api.delete(`/tasks/recurring/feedbacks/${feedbackId}`, { data: { userId } });
  },

  // Recurring Task Finish Detail APIs
  addRecurringTaskFinishDetail: async (templateId: string, content: string, authorId: string): Promise<any> => {
    const response = await api.post(`/tasks/recurring/${templateId}/finish-details`, { content, authorId });
    return response.data;
  },

  getRecurringTaskFinishDetails: async (templateId: string): Promise<any[]> => {
    const response = await api.get(`/tasks/recurring/${templateId}/finish-details`);
    return response.data;
  },

  deleteRecurringTaskFinishDetail: async (finishDetailId: string, userId: string): Promise<void> => {
    await api.delete(`/tasks/recurring/finish-details/${finishDetailId}`, { data: { userId } });
  },
};

// Learning API
const learningAPI = {
  // User/Manager Learning Access
  getMyLearningSections: async (): Promise<any[]> => {
    const response = await api.get('/learning/my-sections');
    return response.data;
  },

  // Admin Learning Sections
  createSection: async (sectionData: any): Promise<any> => {
    const response = await api.post('/learning/sections', sectionData);
    return response.data;
  },

  getAllSections: async (teamId?: string): Promise<any[]> => {
    const params = teamId ? { teamId } : {};
    const response = await api.get('/learning/sections', { params });
    return response.data;
  },

  getSection: async (id: string): Promise<any> => {
    const response = await api.get(`/learning/sections/${id}`);
    return response.data;
  },

  updateSection: async (id: string, sectionData: any): Promise<any> => {
    const response = await api.patch(`/learning/sections/${id}`, sectionData);
    return response.data;
  },

  deleteSection: async (id: string): Promise<void> => {
    await api.delete(`/learning/sections/${id}`);
  },

  // Learning Videos
  addVideo: async (videoData: any): Promise<any> => {
    const response = await api.post('/learning/videos', videoData);
    return response.data;
  },

  getAllVideos: async (sectionId?: string): Promise<any[]> => {
    const params = sectionId ? { sectionId } : {};
    const response = await api.get('/learning/videos', { params });
    return response.data;
  },

  getVideo: async (id: string): Promise<any> => {
    const response = await api.get(`/learning/videos/${id}`);
    return response.data;
  },

  updateVideo: async (id: string, videoData: any): Promise<any> => {
    const response = await api.patch(`/learning/videos/${id}`, videoData);
    return response.data;
  },

  deleteVideo: async (id: string): Promise<void> => {
    await api.delete(`/learning/videos/${id}`);
  },

  // New Learning Content API (supports multiple content types)
  addContent: async (contentData: FormData): Promise<any> => {
    const response = await api.post('/learning/content', contentData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAllContent: async (sectionId?: string): Promise<any[]> => {
    const params = sectionId ? { sectionId } : {};
    const response = await api.get('/learning/content', { params });
    return response.data;
  },

  getContentById: async (id: string): Promise<any> => {
    const response = await api.get(`/learning/content/${id}`);
    return response.data;
  },

  updateContent: async (id: string, contentData: FormData): Promise<any> => {
    const response = await api.patch(`/learning/content/${id}`, contentData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteContent: async (id: string): Promise<void> => {
    await api.delete(`/learning/content/${id}`);
  },

  // Helper function to get file as blob URL with authentication
  getFileBlob: async (contentId: string): Promise<string> => {
    try {
      const response = await api.get(`/learning/content/${contentId}/file`, {
        responseType: 'blob',
      });
      return window.URL.createObjectURL(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('File not found. The file may have been deleted or moved.');
      }
      throw error;
    }
  },

  // Helper function to download file with authentication
  downloadFile: async (contentId: string, fileName?: string): Promise<void> => {
    try {
      const response = await api.get(`/learning/content/${contentId}/file`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw new Error('Failed to download file');
    }
  },
};

// Settings API
export const settingsAPI = {
  getSystemSettings: async (): Promise<any> => {
    const response = await api.get('/settings/system');
    return response.data;
  },

  updateSystemSettings: async (settings: any): Promise<any> => {
    const response = await api.put('/settings/system', settings);
    return response.data;
  },

  getUserSettings: async (userId: string): Promise<any> => {
    const response = await api.get(`/settings/user/${userId}`);
    return response.data;
  },

  updateUserSettings: async (userId: string, settings: any): Promise<any> => {
    const response = await api.put(`/settings/user/${userId}`, settings);
    return response.data;
  },

  getNotificationSettings: async (userId: string): Promise<any> => {
    const response = await api.get(`/settings/notifications/${userId}`);
    return response.data;
  },

  updateNotificationSettings: async (userId: string, settings: any): Promise<any> => {
    const response = await api.put(`/settings/notifications/${userId}`, settings);
    return response.data;
  },

  exportData: async (): Promise<any> => {
    const response = await api.get('/settings/backup/export');
    return response.data;
  },

  importData: async (data: any): Promise<any> => {
    const response = await api.post('/settings/backup/import', data);
    return response.data;
  },

  getAnalyticsSummary: async (): Promise<any> => {
    const response = await api.get('/settings/analytics/summary');
    return response.data;
  },
};

// Projects API
const projectsAPI = {
  getAll: async (params?: { teamId?: string; status?: string; creatorId?: string; limit?: number; isArchived?: boolean }): Promise<any[]> => {
    const searchParams = new URLSearchParams();
    if (params?.teamId) searchParams.append('teamId', params.teamId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.creatorId) searchParams.append('creatorId', params.creatorId);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.isArchived !== undefined) searchParams.append('isArchived', params.isArchived.toString());

    const response = await api.get(`/projects?${searchParams}`);
    return response.data;
  },

  getById: async (id: string): Promise<any> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  create: async (projectData: any): Promise<any> => {
    const response = await api.post('/projects', projectData);
    return response.data;
  },

  update: async (id: string, projectData: any): Promise<any> => {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },

  delete: async (id: string): Promise<any> => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },

  archive: async (id: string): Promise<any> => {
    const response = await api.put(`/projects/${id}/archive`);
    return response.data;
  },

  restore: async (id: string): Promise<any> => {
    const response = await api.put(`/projects/${id}/restore`);
    return response.data;
  },

  getProjectTasks: async (id: string): Promise<any[]> => {
    const response = await api.get(`/projects/${id}/tasks`);
    return response.data;
  },

  getProjectRecurringTasks: async (id: string): Promise<any[]> => {
    const response = await api.get(`/projects/${id}/recurring-tasks`);
    return response.data;
  },

  getProjectAnalytics: async (id: string): Promise<any> => {
    const response = await api.get(`/projects/${id}/analytics`);
    return response.data;
  },

  getAnalytics: async (): Promise<any> => {
    const response = await api.get('/projects/analytics');
    return response.data;
  },

  addTaskToProject: async (projectId: string, taskData: any): Promise<any> => {
    const response = await api.post(`/projects/${projectId}/tasks`, taskData);
    return response.data;
  },

  removeTaskFromProject: async (projectId: string, taskId: string): Promise<any> => {
    const response = await api.delete(`/projects/${projectId}/tasks/${taskId}`);
    return response.data;
  },
};

// Analytics API
const analyticsAPI = {
  getOverview: async (): Promise<any> => {
    const response = await api.get('/analytics/overview');
    return response.data;
  },

  getProjectAnalytics: async (projectId?: string): Promise<any> => {
    const url = projectId ? `/analytics/projects/${projectId}` : '/analytics/projects';
    const response = await api.get(url);
    return response.data;
  },

  getTeamAnalytics: async (teamId?: string): Promise<any> => {
    const url = teamId ? `/analytics/teams/${teamId}` : '/analytics/teams';
    const response = await api.get(url);
    return response.data;
  },

  getGanttData: async (): Promise<any> => {
    const response = await api.get('/analytics/gantt');
    return response.data;
  },

  getMindMapData: async (): Promise<any> => {
    const response = await api.get('/analytics/mindmap');
    return response.data;
  },
};

export { authAPI, usersAPI, teamsAPI, tasksAPI, projectsAPI, analyticsAPI, learningAPI };
export default api;
